import { motion } from 'framer-motion';

const phases = [
  {
    phase: 'Fase 1',
    title: 'Legal Tech + B2B Abogados',
    period: 'Q2-Q4 2026',
    status: 'active' as const,
    items: [
      'Migro deja de gestionar expedientes y los delega a abogados profesionales',
      'Abogados pagan cuota semanal (149-499 EUR) por recibir clientes cualificados',
      'Landing de captacion de abogados con conversion optimizada',
      'Primeros 50-100 abogados suscritos a traves de agencias partner',
      'La app del migrante se convierte en el punto de entrada a todo',
    ],
    target: 'Facturacion > 90K EUR/mes',
  },
  {
    phase: 'Fase 2',
    title: 'Fintech — Cuenta bancaria + Remesas',
    period: 'Q1-Q2 2027',
    status: 'pending' as const,
    items: [
      'Cuenta bancaria espanola (ES-IBAN) con solo el pasaporte, desde el dia 1',
      'Envio de dinero a LATAM hasta 75% mas barato que Western Union',
      'Blockchain como infraestructura invisible: el usuario solo ve euros',
      'Tarjeta Visa/Mastercard fisica y virtual en Europa',
      'Tramites de licencias financieras propias en paralelo',
    ],
    target: 'Ingresos por remesas > 30K EUR/mes',
  },
  {
    phase: 'Fase 3',
    title: 'Tarjetas en Latinoamerica',
    period: 'Q3-Q4 2027',
    status: 'pending' as const,
    items: [
      'La familia del migrante recibe una tarjeta en su pais, en su moneda',
      'El dinero llega en minutos, no en dias',
      'Planes premium para usuarios frecuentes (4.99-9.99 EUR/mes)',
      'Liquidez pre-fondeada en cada region para velocidad instantanea',
    ],
    target: 'Ingresos combinados > 500K EUR/mes',
  },
  {
    phase: 'Fase 4',
    title: 'Expansion a toda Europa',
    period: '2028+',
    status: 'pending' as const,
    items: [
      'Mismo modelo en Portugal, Italia y Francia — misma regulacion EU',
      'Pili IA aprende la legislacion de cada pais automaticamente',
      'Agencias de marketing locales como canal de entrada en cada mercado',
      'Pili se vende como API independiente a despachos grandes',
    ],
    target: 'Ingresos > 2M EUR/mes',
  },
];

const statusStyles: Record<string, { dot: string; line: string; text: string }> = {
  active: { dot: 'border-emerald-400 bg-emerald-400 ring-4 ring-emerald-400/20', line: 'bg-gradient-to-b from-emerald-400 to-white/10', text: 'text-emerald-400' },
  pending: { dot: 'border-white/20 bg-white/5', line: 'bg-white/10', text: 'text-slate-500' },
};

export function ProductRoadmap() {
  return (
    <section id="roadmap" className="py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-emerald-400">
            Hacia donde vamos
          </span>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            De tramites legales a la{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">
              infraestructura del migrante
            </span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-slate-400">
            Cada fase anade una fuente de ingresos recurrente y aumenta el valor del ecosistema para todos.
          </p>
        </motion.div>

        <div className="relative mx-auto max-w-3xl">
          {phases.map((phase, i) => {
            const s = statusStyles[phase.status];
            const isLast = i === phases.length - 1;

            return (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className="relative flex gap-6 pb-10"
              >
                {/* Timeline */}
                <div className="flex flex-col items-center">
                  <div className={`h-8 w-8 shrink-0 rounded-full border-2 ${s.dot} flex items-center justify-center`}>
                    <span className={`text-xs font-bold ${phase.status === 'active' ? 'text-slate-900' : 'text-slate-500'}`}>{i + 1}</span>
                  </div>
                  {!isLast && <div className={`mt-1 h-full w-0.5 ${s.line}`} />}
                </div>

                {/* Content */}
                <div className="flex-1 rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-md">
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className={`text-xs font-bold uppercase tracking-wider ${s.text}`}>{phase.phase}</span>
                    <span className="text-xs text-slate-500">|</span>
                    <span className="text-xs text-slate-400">{phase.period}</span>
                    {phase.status === 'active' && (
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        </span>
                        En progreso
                      </span>
                    )}
                  </div>
                  <h3 className="mb-3 font-display text-lg font-bold text-white">{phase.title}</h3>
                  <ul className="space-y-1.5">
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 rounded-lg border border-emerald-500/20 bg-emerald-500/[0.08] px-3 py-2">
                    <p className="text-xs font-semibold text-emerald-400">Target: {phase.target}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
